/**
 * **Upload is a popup over the current screen** (W22, FR-59, plan D57).
 *
 * One popup, everywhere: the rail's *Upload*, the *Upload* buttons on Home and
 * Documents and Go To's *Upload documents* all call `openUpload()` from here.
 * The popup is the package's `ProDocUploadDialog` — today's uploader inside the
 * kit's dialog at its widest — and **closing it stops nothing**: the queue is a
 * module store that outlives any screen (`upload-queue.ts`), so the package's
 * `ProDocUploadTray` keeps the count in the corner and reopens the popup with
 * every row as it was, and moving to another ProDoc screen keeps them going.
 *
 * Three things leave the files behind, and each is answered here:
 *
 * - **closing or reloading the tab** — the package's `useLeaveWarning` asks the
 *   browser to warn while a file is not yet stored;
 * - **switching organisation** and **signing out** — the shell's org switcher,
 *   Go To's *Switch to …* and the account menu's *Sign out* come through
 *   `switchOrg` / `signOut`, which ask first while a file is in flight. Going
 *   ahead stops the files not yet sent (`stopUploads`) — this organisation's on
 *   a switch, every one on a sign-out — and leaves the stored ones where they
 *   are; a request already on its way cannot be called back.
 *
 * Either way the passwords kept for this tab's pages are forgotten
 * (`passwordKeeper.clear()`, FR-58, D56) — they never outlive the organisation
 * or the person who typed them. `lib/session-end.ts` forgets them as well when
 * the session ends some other way.
 *
 * **Into the folder you're in.** Documents opens the popup with the workspace
 * and folder being browsed; elsewhere the uploader's own defaults hold. The
 * popup closes when the screen changes underneath it — a stored file's link
 * opens its document — and the tray keeps counting.
 *
 * **Full screen on a phone** (W25, FR-59): the popup is opened with `sheet`,
 * the kit's `Dialog` filling the screen below the `sm` width.
 */
import { ProDocUploadDialog, ProDocUploadTray, passwordKeeper, stopUploads, uploadsInFlight, useLeaveWarning } from '@fiveox/prodoc-ui'
import { ConfirmDialog } from '@fiveox/ui'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'

import { APP_SLUG, SERVICE_API, STORE_URL } from '../lib/config'
import { session } from '../lib/session'

/** Where the popup files by default, and the files it opens with. */
export interface UploadAt {
  workspaceId?: string | null
  folderId?: string | null
  /** Dropped onto a screen before the popup was open: added as it opens. */
  files?: File[]
  /** A dropped folder's entries (`entriesOf`, taken in the drop): added with its subfolders, as a folder drop is. */
  entries?: FileSystemEntry[]
}

export interface UploadHost {
  /** Open the popup — the same one from every entry point. Nothing happens for a person who may not upload. */
  openUpload: (at?: UploadAt) => void
  /** Whether the popup is open: a screen's own drop target yields to the uploader's while it is. */
  isOpen: boolean
  /** Whether this person may upload (`document:can_create`) — every entry point is drawn only then. */
  canUpload: boolean
  /** Switch organisation — asking first while a file is in flight. */
  switchOrg: (orgId: string) => void
  /** Sign out — asking first while a file is in flight. */
  signOut: () => void
}

const UploadContext = createContext<UploadHost | null>(null)

/** Only valid inside `<UploadProvider>`, which `App.tsx` renders around the shell. */
export function useUpload(): UploadHost {
  const value = useContext(UploadContext)
  if (!value) throw new Error('useUpload() must be used inside <UploadProvider>.')
  return value
}

/** What a person is about to do that would leave a file behind. */
type Leaving = { kind: 'switch'; orgId: string; name: string } | { kind: 'sign_out' }

export interface UploadProviderProps {
  orgId: string
  /** The organisations a switch may go to, for the question's wording. */
  orgs: { id: string; display_name: string }[]
  userId: string | null
  canUpload: boolean
  /** The switch itself — `setOrgId`. */
  onSwitchOrg: (orgId: string) => void
  /** The sign-out itself — the shell's `signOutEverywhere`. */
  onSignOut: () => void
  /** The shell, drawn with the host's guarded switch and sign-out. */
  children: (host: UploadHost) => ReactNode
}

export function UploadProvider({ orgId, orgs, userId, canUpload, onSwitchOrg, onSignOut, children }: UploadProviderProps) {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const [at, setAt] = useState<UploadAt>({})
  const [leaving, setLeaving] = useState<Leaving | null>(null)
  const [inFlight, setInFlight] = useState(0)
  /** The screen the popup was opened on — a ref, so a child screen's effect that opens it is seen at once. */
  const openedOn = useRef<string | null>(null)

  // Closing or reloading the tab warns while a file is not yet stored.
  useLeaveWarning(APP_SLUG)

  // The screen changed underneath the popup (a stored file's link, the rail): it closes; the tray keeps counting.
  useEffect(() => {
    if (openedOn.current !== null && openedOn.current !== pathname) {
      openedOn.current = null
      setOpen(false)
      setAt((a) => ({ ...a, files: undefined, entries: undefined }))
    }
  }, [pathname])

  const openUpload = useCallback(
    (next: UploadAt = {}) => {
      if (!canUpload) return
      openedOn.current = pathname
      setAt({
        workspaceId: next.workspaceId ?? null,
        folderId: next.folderId ?? null,
        files: next.files?.length ? next.files : undefined,
        entries: next.entries?.length ? next.entries : undefined,
      })
      setOpen(true)
    },
    [canUpload, pathname],
  )

  /** Reopened from the tray: the rows are the store's, the defaults as they were, no files added again. */
  const reopen = useCallback(() => {
    openedOn.current = pathname
    setAt((a) => ({ ...a, files: undefined, entries: undefined }))
    setOpen(true)
  }, [pathname])

  const close = useCallback(() => {
    openedOn.current = null
    setOpen(false)
    setAt((a) => ({ ...a, files: undefined, entries: undefined }))
  }, [])

  const go = useCallback(
    (act: Leaving) => {
      setLeaving(null)
      close()
      // The files not yet sent stop — this organisation's on a switch, every one on a sign-out; the stored ones stay.
      stopUploads(APP_SLUG, act.kind === 'switch' ? orgId : undefined)
      // A typed password belongs to this organisation and this person, and to nobody after them (D56).
      passwordKeeper.clear()
      if (act.kind === 'switch') onSwitchOrg(act.orgId)
      else onSignOut()
    },
    [close, onSignOut, onSwitchOrg, orgId],
  )

  const leave = useCallback(
    (act: Leaving) => {
      // A switch leaves this organisation's files behind; a sign-out leaves every one of this tab's.
      const count = act.kind === 'switch' ? uploadsInFlight(APP_SLUG, orgId) : uploadsInFlight(APP_SLUG)
      if (count > 0) {
        setInFlight(count)
        setLeaving(act)
        return
      }
      go(act)
    },
    [go, orgId],
  )

  const switchOrg = useCallback(
    (id: string) => {
      if (id === orgId) return
      leave({ kind: 'switch', orgId: id, name: orgs.find((o) => o.id === id)?.display_name ?? 'the other organisation' })
    },
    [leave, orgId, orgs],
  )
  const signOut = useCallback(() => leave({ kind: 'sign_out' }), [leave])

  const host = useMemo<UploadHost>(() => ({ openUpload, isOpen: open, canUpload, switchOrg, signOut }), [openUpload, open, canUpload, switchOrg, signOut])
  const files = inFlight === 1 ? 'One file is' : `${inFlight} files are`

  return (
    <UploadContext.Provider value={host}>
      {children(host)}
      {canUpload && (
        <ProDocUploadDialog
          open={open}
          onClose={close}
          // Full screen below `sm` (W25, FR-59's phone mode); the dialog at its widest above it.
          sheet
          initialFiles={at.files}
          initialEntries={at.entries}
          workspaceId={at.workspaceId ?? undefined}
          folderId={at.folderId ?? undefined}
          apiUrl={SERVICE_API}
          fetcher={session.authed}
          orgId={orgId}
          userId={userId}
          ownerApp={APP_SLUG}
          documentHref={(doc) => `/documents/${doc.id}`}
          renderLink={(href, content, className) => (
            <Link to={href} className={className}>
              {content}
            </Link>
          )}
          storeUrl={`${STORE_URL}/catalog/${APP_SLUG}`}
        />
      )}
      {/* This organisation's files: the tray agrees with the rows the popup it reopens shows. */}
      <ProDocUploadTray ownerApp={APP_SLUG} orgId={orgId} onOpen={reopen} />
      <ConfirmDialog
        open={leaving !== null}
        onClose={() => setLeaving(null)}
        title={leaving?.kind === 'switch' ? `Switch to ${leaving.name}?` : 'Sign out?'}
        confirmLabel={leaving?.kind === 'switch' ? 'Switch anyway' : 'Sign out anyway'}
        cancelLabel="Keep uploading"
        onConfirm={() => leaving && go(leaving)}
      >
        <p>
          {files} not stored yet. {leaving?.kind === 'switch' ? 'Switching organisation' : 'Signing out'} stops {inFlight === 1 ? 'it' : 'them'}; the
          files already stored stay where they were filed.
        </p>
      </ConfirmDialog>
    </UploadContext.Provider>
  )
}
